import { JSX, KeyboardEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { MAX_RECORDING_SECONDS, useVoiceInput, VoiceInput } from '../hooks/use-voice-input';
import { ChatTranscript } from '../context/ChatBotContextProvider';

/**
 * How tall the box is before anything is typed.
 *
 * It was one line, which is enough to see what you have written and not enough to write it. A
 * question worth asking the assistant is usually a sentence or two with a name in it, and a
 * one-line box makes that feel like the wrong place to put it.
 */
const MIN_INPUT_HEIGHT_PX: number = 78;

/** Beyond this the textarea scrolls rather than growing further. */
const MAX_INPUT_HEIGHT_PX: number = 260;

interface ComposerProps {
    disabled: boolean;
    busy: boolean;
    onSend: (message: string) => void;
    onRecord: (audio: string, mime: string) => void;
    /** Stops the reply being written. Send becomes Stop while `busy`. */
    onStop: () => void;
    /** True once Stop has been pressed and the reply has not yet ended. */
    stopping: boolean;
    /** True while a clip is with the Operator. */
    transcribing: boolean;
    transcript: ChatTranscript | null;
}

export const ChatComposer: (props: ComposerProps) => JSX.Element =
    ({ disabled, busy, onSend, onRecord, onStop, stopping, transcribing, transcript }): JSX.Element => {
        const [draft, setDraft] = useState<string>('');
        const input: RefObject<HTMLTextAreaElement | null> = useRef<HTMLTextAreaElement>(null);
        const lastTranscriptAt = useRef<number>(0);

        const handleClip = useCallback(
            (audio: string, mime: string): void => onRecord(audio, mime),
            [onRecord]
        );
        const voice: VoiceInput = useVoiceInput(handleClip);

        // Grow with the content, between the two bounds above. The floor is applied here as well
        // as in the stylesheet because this writes an inline height on every keystroke, and
        // clearing the box would otherwise collapse it to a single line until the next one.
        useEffect((): void => {
            const node: HTMLTextAreaElement | null = input.current;
            if (node === null) {
                return;
            }
            node.style.height = 'auto';
            const wanted: number = Math.min(node.scrollHeight, MAX_INPUT_HEIGHT_PX);
            node.style.height = Math.max(wanted, MIN_INPUT_HEIGHT_PX) + 'px';
        }, [draft]);

        /**
         * Put what was dictated into the box, and leave it there (user, 2026-09-08).
         *
         * It was briefly sent straight into the conversation, the same as a typed message. Held
         * for review instead: transcription gets names wrong, and a question silently asked on
         * your behalf is worse than one you can see and correct before pressing Send.
         *
         * Appended rather than assigned, so dictating twice - or dictating after typing - adds to
         * the thought instead of destroying it.
         */
        useEffect((): void => {
            if (transcript === null || transcript.at === lastTranscriptAt.current || transcript.text === '') {
                return;
            }
            lastTranscriptAt.current = transcript.at;
            setDraft((previous: string): string =>
                previous === '' ? transcript.text : previous.trimEnd() + ' ' + transcript.text
            );
            input.current?.focus();
        }, [transcript]);

        function send(): void {
            const message: string = draft.trim();
            if (message === '' || disabled) {
                return;
            }
            onSend(message);
            setDraft('');
        }

        function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
            // Enter sends, Shift+Enter makes a new line. The old form could only be sent by
            // reaching for the mouse.
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
            }
        }

        function placeholder(): string {
            if (voice.recording) {
                return 'Listening...';
            }
            if (transcribing) {
                return 'Writing that down...';
            }
            return busy ? 'Waiting for a reply...' : 'Ask a question';
        }

        return (
            <div className="chat__composer">
                { voice.error !== '' && (
                    <p className="composer__notice">{ voice.error }</p>
                ) }

                {/*
                  * The box has the row to itself and the controls sit under it. Sharing a row
                  * with the microphone and Send cost it about a third of a 420px panel, which is
                  * the width a question is actually written in.
                  */}
                <textarea
                    ref={ input }
                    className="composer__input"
                    value={ draft }
                    rows={ 3 }
                    placeholder={ placeholder() }
                    disabled={ disabled }
                    onChange={ (event): void => setDraft(event.target.value) }
                    onKeyDown={ handleKeyDown }
                />

                <div className="composer__controls">
                    {/*
                      * Shift+Enter matters more now there is room for a second line, and it is
                      * not something anyone discovers by looking. That Enter sends is the
                      * expected half and is already on the Send button, so only this half is
                      * spelled out - short enough to survive the narrowest the panel resizes to.
                      */}
                    <span className="composer__hint">Shift + Enter for a new line</span>

                    { voice.supported && (
                        <button
                            type="button"
                            className={ 'composer__mic' + (voice.recording ? ' is-recording' : '') }
                            disabled={ disabled || transcribing }
                            onClick={ voice.recording ? voice.stop : voice.start }
                            title={ voice.recording
                                ? 'Stop recording'
                                : 'Dictate a question (up to ' + MAX_RECORDING_SECONDS + ' seconds)' }
                            aria-label={ voice.recording ? 'Stop recording' : 'Dictate a question' }
                        >
                            { voice.recording
                                ? <StopCircleOutlinedIcon fontSize="small" />
                                : <MicNoneOutlinedIcon fontSize="small" /> }
                            { voice.recording && (
                                <span className="mic__elapsed">
                                    { MAX_RECORDING_SECONDS - voice.seconds }
                                </span>
                            ) }
                        </button>
                    ) }

                    {/*
                      * DAI-07. Stop takes Send's place rather than sitting beside it: while a reply
                      * is being written there is nothing to send, and the button under the pointer
                      * is the one that should answer "make it stop". Not tied to `disabled`, which
                      * is true for the whole of a reply and is exactly when this is needed.
                      */}
                    { busy ? (
                        <button
                            type="button"
                            className="composer__send is-stop"
                            disabled={ stopping }
                            onClick={ onStop }
                            title="Stop the reply"
                        >
                            { stopping ? 'Stopping' : 'Stop' }
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="composer__send"
                            disabled={ disabled || draft.trim() === '' }
                            onClick={ send }
                            title="Send (Enter)"
                        >
                            Send
                        </button>
                    ) }
                </div>
            </div>
        );
    };
